import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from './Auth'
import { AuthTokenResponse } from './_models'

/**
 * Estado que o login passa às telas de segundo fator pelo state da rota.
 *
 * Fica em memória de propósito: o desafio é credencial de curta duração e não pode ir para
 * o localStorage. Ele morre no reload, e nesse caso as telas devolvem o usuário ao login.
 */
export type TwoFactorState = {
  desafio: string
}

export const useTwoFactorState = (): TwoFactorState | undefined => {
  const { state } = useLocation()
  const twoFactor = state as TwoFactorState | null

  return twoFactor?.desafio ? twoFactor : undefined
}

/**
 * Conclui o login depois que o segundo fator foi aceito. É o mesmo encerramento do login
 * direto: guarda a sessão, carrega o usuário e desvia para a troca de senha ou para o termo de
 * ciência (LGPD) se for o caso — nessa ordem, que é a dos filtros do backend.
 *
 * O identificador de push não é gravado aqui: o usePushNotifications reenvia o FID ao iniciar a
 * sessão e é quem troca o valor guardado no localStorage, revogando antes o anterior.
 */
export const useConcluirLogin = () => {
  const { saveAuth, reloadUser } = useAuth()
  const navigate = useNavigate()

  return async (auth: AuthTokenResponse) => {
    saveAuth(auth)
    await reloadUser()
    if (auth.trocarSenha) navigate('/reset-password')
    else if (auth.termoCienciaPendente) navigate('/termo-ciencia')
  }
}

export type ErroMfa = {
  mensagem: string
  /** O desafio morreu no servidor: repetir o código não adianta, só refazer o login. */
  desafioEncerrado: boolean
}

export const ERRO_SESSAO_MFA =
  'Seu código foi aceito, mas não foi possível carregar a sessão. Tente entrar novamente.'

const MARCADOR_DESAFIO_ENCERRADO = 'login novamente'

/**
 * O 401 do backend traz a razão real no corpo (`{ data: { message } }`, ou a lista de `errors`
 * que o `buildErrorMessage` percorre). Sem lê-la, um desafio expirado ou uma conta bloqueada
 * apareceriam para o usuário como "código inválido".
 */
export const interpretarErroMfa = (error: any, fallback: string): ErroMfa => {
  const corpo = error?.response?.data?.data ?? error?.response?.data
  const detalhes = Object.values(corpo?.errors ?? {}).flat() as string[]
  const mensagem = detalhes[0] || corpo?.message || fallback

  return {
    mensagem,
    desafioEncerrado: mensagem.toLowerCase().includes(MARCADOR_DESAFIO_ENCERRADO),
  }
}
